// src/components/Pentests/runs/WebCrawlerView.js
//
// Web crawler results: URL list with status, title, content-type, and
// any links discovered. Designed for thorough viewing of dense crawl output.

import { useMemo, useState } from 'react';
import { ExternalLink, Globe2, Search } from 'lucide-react';

const STATUS_COLOR = (s) => {
  if (!s) return 'text-slate-500';
  const n = Number(s);
  if (n >= 500) return 'text-red-700';
  if (n >= 400) return 'text-orange-700';
  if (n >= 300) return 'text-yellow-700';
  if (n >= 200) return 'text-shinobi-green';
  return 'text-slate-500';
};

export default function WebCrawlerView({ results }) {
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    return results.map((r) => {
      const d = r.data || {};
      return {
        id: r.id,
        url: r.value,
        status: d.status_code || d.status || '',
        title: d.title || '',
        contentType: d.content_type || d.contentType || '',
        contentLength: d.content_length || d.contentLength || null,
        method: d.method || 'GET',
      };
    });
  }, [results]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.url, String(r.status), r.title, r.contentType].some((s) =>
        String(s).toLowerCase().includes(q)
      )
    );
  }, [rows, filter]);

  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <Globe2 className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No URLs crawled yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter URLs, status codes, titles..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === rows.length ? `${rows.length} URLs` : `${filtered.length} of ${rows.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        <table className="w-full text-sm text-left">
          <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
            <tr>
              <th className="px-4 py-3 w-16">Status</th>
              <th className="px-4 py-3">URL</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Content-Type</th>
              <th className="px-4 py-3 text-right">Size</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-cyber-300/30 hover:bg-cyber-200/40">
                <td className={`px-4 py-2 font-mono text-xs ${STATUS_COLOR(r.status)}`}>
                  {r.status || '-'}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-slate-800 break-all max-w-xl">
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-shinobi-green inline-flex items-center gap-1"
                  >
                    {r.url}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                </td>
                <td className="px-4 py-2 text-xs text-slate-700 max-w-xs truncate">{r.title || '-'}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{r.contentType || '-'}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500 text-xs">
                  {r.contentLength ? formatBytes(r.contentLength) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatBytes(n) {
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}
